import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  /** @type {HTMLElement | null} */
  #customAnimationContainer = null;
  #customDrawerOffsetRaf = 0;
  #customDrawerOffsetPostRaf = 0;
  #boundUpdateCustomDrawerOffset = () => this.#updateCustomDrawerTopOffset();

  connectedCallback() {
    super.connectedCallback();

    this.#customAnimationContainer = this.closest('[data-custom-animation="true"]');

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    if (this.#customAnimationContainer) {
      this.#scheduleCustomDrawerTopOffsetUpdate();
      window.addEventListener('resize', this.#boundUpdateCustomDrawerOffset, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);

    if (this.#customDrawerOffsetRaf) {
      cancelAnimationFrame(this.#customDrawerOffsetRaf);
      this.#customDrawerOffsetRaf = 0;
    }

    if (this.#customDrawerOffsetPostRaf) {
      cancelAnimationFrame(this.#customDrawerOffsetPostRaf);
      this.#customDrawerOffsetPostRaf = 0;
    }

    if (this.#customAnimationContainer) {
      window.removeEventListener('resize', this.#boundUpdateCustomDrawerOffset);
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#scheduleCustomDrawerTopOffsetUpdate();

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      this.#updateCustomDrawerTopOffset();
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Measure once immediately, then again across the next paint frames so
   * sticky/header layout changes are reflected before first open.
   */
  #scheduleCustomDrawerTopOffsetUpdate() {
    if (!this.#customAnimationContainer) return;

    this.#updateCustomDrawerTopOffset();

    if (this.#customDrawerOffsetRaf) {
      cancelAnimationFrame(this.#customDrawerOffsetRaf);
    }

    if (this.#customDrawerOffsetPostRaf) {
      cancelAnimationFrame(this.#customDrawerOffsetPostRaf);
      this.#customDrawerOffsetPostRaf = 0;
    }

    this.#customDrawerOffsetRaf = requestAnimationFrame(() => {
      this.#customDrawerOffsetRaf = 0;
      this.#updateCustomDrawerTopOffset();

      this.#customDrawerOffsetPostRaf = requestAnimationFrame(() => {
        this.#customDrawerOffsetPostRaf = 0;
        this.#updateCustomDrawerTopOffset();
      });
    });
  }

  /**
   * Keep custom animated drawer aligned below the top header row.
   */
  #updateCustomDrawerTopOffset() {
    if (!this.#customAnimationContainer) return;

    const header = this.closest('.header') || this.ownerDocument.querySelector('#header-component .header');
    const topRow = header?.querySelector('.header__row--top');

    if (!(topRow instanceof HTMLElement)) {
      this.style.setProperty('--custom-mobile-drawer-top-offset', '0px');
      return;
    }

    const topRowRect = topRow.getBoundingClientRect();
    const topRowColumns = topRow.querySelector('.header__columns');
    const columnsBottom = topRowColumns instanceof HTMLElement ? topRowColumns.getBoundingClientRect().bottom : topRowRect.bottom;

    // Use the lowest visible edge and round up so the drawer always sits just below the row.
    const topOffset = Math.max(0, Math.ceil(Math.max(topRowRect.bottom, columnsBottom)));
    this.style.setProperty('--custom-mobile-drawer-top-offset', `${topOffset}px`);
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
